import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  Typography
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FullScreenIcon from "@mui/icons-material/Fullscreen";
import FullScreenExitIcon from "@mui/icons-material/FullscreenExit";
import startCase from "lodash/startCase";
import { GROUP_BOOKINGS_BY, GroupBookingsByType } from "./index";
import { tl } from "../../../components/translate";

interface HeaderInterfaceProps {
  fullscreenActive: boolean;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  showByStatus: boolean;
  setShowByStatus: () => void;
  onGroupBookingByChange: (value: GroupBookingsByType) => void;
  groupBookingsBy: GroupBookingsByType;
  showFollowUpOnly: boolean;
  setShowFollowUpOnly: React.Dispatch<React.SetStateAction<boolean>>;
}

const Header = ({
  fullscreenActive,
  exitFullscreen,
  enterFullscreen,
  showByStatus,
  setShowByStatus,
  onGroupBookingByChange,
  groupBookingsBy,
  setShowFollowUpOnly,
  showFollowUpOnly
}: HeaderInterfaceProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, md: 0 },
        px: "20px",
        py: 1,
        minHeight: "62px"
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "16px", md: "20px" }
        }}
      >
        {tl("dashboard.patientflow")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: { xs: "flex-start", md: "flex-end" }
        }}
      >
        <FormGroup
          sx={{
            display: "flex",
            gap: "10px",
            flexDirection: { xs: "column", md: "row" }
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showFollowUpOnly}
                onClick={() => setShowFollowUpOnly(!showFollowUpOnly)}
                sx={{ p: 0, mr: "5px" }}
              />
            }
            label={
              <Typography variant="h5" sx={{ fontSize: "12px" }}>
                {tl("dashboard.showFollowUpOnly")}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showByStatus}
                onClick={setShowByStatus}
                sx={{ p: 0, mr: "5px" }}
              />
            }
            label={
              <Typography variant="h5" sx={{ fontSize: "12px" }}>
                {tl("dashboard.showbystatus")}
              </Typography>
            }
          />
        </FormGroup>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              background: "#F03636",
              borderRadius: "50%",
              width: "8px",
              height: "8px",
              marginRight: "4px"
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "18px",
              marginRight: "24px"
            }}
          >
            Live
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "30px",
              height: "30px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "50%",
              "&:hover": { background: "#F1FFF3" }
            }}
          >
            {fullscreenActive ? (
              <FullScreenExitIcon
                onClick={exitFullscreen}
                sx={{ color: "rgba(0, 0, 0, 0.54)", height: "18px", width: "18px" }}
              />
            ) : (
              <FullScreenIcon
                onClick={enterFullscreen}
                sx={{ color: "rgba(0, 0, 0, 0.54)", height: "18px", width: "18px" }}
              />
            )}
          </Box>
          <Box sx={{ mr: -3 }}>
            <IconButton onClick={handleClick} size="small">
              <MoreVertIcon />
            </IconButton>
            <Menu
              disablePortal
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {Object.values(GROUP_BOOKINGS_BY).map((item) => (
                <MenuItem
                  key={item}
                  dense
                  onClick={() => {
                    handleClose();
                    if (item !== groupBookingsBy) onGroupBookingByChange(item);
                  }}
                >
                  <FormControlLabel
                    label={`Group By ${startCase(item)}`}
                    control={<Radio size="small" checked={groupBookingsBy === item} />}
                  />
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
